import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { faCirclePlay, faComment, faCalendar, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, CircularProgress } from '@mui/material';
import { toast, ToastContainer } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import { useDispatch, useSelector } from 'react-redux';
import { getFetchGamePostFileThumbnail, getVideos } from '../../../redux/network/videoApi';
import thumbnail from './thumbnail.jpg';
import { useNavigate, useLocation } from 'react-router-dom';
import SearchVideos from "../../SearchBoxes/SearchVideos";
import { SEARCH_BOX } from "../../../constant"
import SearchUploadedVideoPagination from '../../Pagination/SearchUploadedVideoPagination/SearchUploadedVideoPagination';
import CustomDropdown from '../../CustomeDropdown/CustomDropdown';
import { usePagination } from '../../../hooks/usePagination';
import ReactGA from 'react-ga4';

// Set limit to 12
const VIDEO_SEARCH_API_LIMIT = 12;

const dataFormatter = (dateString) => {
    const inputDate = new Date(dateString);
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const day = inputDate.getDate();
    const monthIndex = inputDate.getMonth();
    const year = inputDate.getFullYear();
    const hours = inputDate.getHours();
    const minutes = inputDate.getMinutes();
    return `${day} ${monthNames[monthIndex]} ${year} • ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

const extractTitle = (videoPath) => {
    if (!videoPath) return "Unknown Title";
    const filename = videoPath.split('/').pop().replace(/\.[^/.]+$/, "");
    return filename;
};

const truncateTitle = (filename) => {
    if (!filename) return "";
    const words = filename.split(" ");
    return words.length > 6 ? words.slice(0, 6).join(" ") + " .." : filename;
};

const extractPlayerName = (video) => {
    if (!video || !video.poster || !video.poster.profileData || !video.poster.profileData.name) {
        return "Unknown Player";
    }
    const { firstName = "", lastName = "" } = video.poster.profileData.name;
    return `${firstName} ${lastName}`.trim();
};

const Pending = () => {
    const env = process.env.REACT_APP_ENV;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    // Redux selectors
    const { isStarted, isError, error, data, totalNumberOfRecords } = useSelector(state => state.video.video);

    // Custom pagination hook
    const {
        currentPage,
        totalPages,
        searchTerm,
        sortOption,
        goToPage,
        updateSearchTerm,
        updateSortOption
    } = usePagination('pending');

    // Local state
    const [videoThumbnails, setVideoThumbnails] = useState({});
    const [thumbnailLoading, setThumbnailLoading] = useState({});

    // Debug tracking
    const thumbnailsLoadedRef = useRef(false);

    // Derived state
    const jwtToken = localStorage.getItem("BQS_TOKEN");
    const userId = localStorage.getItem("BQS__ID");

    // Sort options
    const sortOptions = [
        { value: 'feedbackPriority', label: 'Sort By' },
        { value: 'feedbackPriority', label: 'Feedback Priority' },
        { value: 'newToOld', label: 'New to Old' },
        { value: 'oldToNew', label: 'Old to New' },
    ];

    // GA: pageview
    useEffect(() => {
        try { ReactGA.send({ hitType: 'pageview', page: pathname || '/coach_flow/pending-video', title: 'Coach Flow - Pending' }); } catch { }
    }, [pathname]);

    // Get sort configuration
    const sortConfig = useMemo(() => {
        switch (sortOption) {
            case 'feedbackPriority':
                return { "coachManualFeedback": 1, "updatedAt": -1 };
            case 'newToOld':
                return { "updatedAt": -1 };
            case 'oldToNew':
                return { "updatedAt": 1 };
            default:
                return { "coachManualFeedback": 1, "updatedAt": -1 };
        }
    }, [sortOption]);

    // Memoize API payload
    const apiPayload = useMemo(() => {
        if (!jwtToken || !userId || env === "standalone") return null;

        return {
            "condition": {
                "state": ["review_requested"],
                "searchTerm": searchTerm,
                "poster": userId
            },
            "sort": sortConfig,
            "skip": currentPage - 1,
            "limit": VIDEO_SEARCH_API_LIMIT,
        };
    }, [jwtToken, userId, searchTerm, sortConfig, currentPage, env]);

    // Fetch videos
    useEffect(() => {
        if (apiPayload) {
            dispatch(getVideos(jwtToken, apiPayload));
        }
    }, [apiPayload, jwtToken, dispatch]);

    // Update pagination state
    useEffect(() => {
        if (totalNumberOfRecords !== undefined) {
            const totalRecords = totalNumberOfRecords;
            dispatch({
                type: 'pagination/updatePaginationFromResponse',
                payload: {
                    totalRecords,
                    currentPage,
                    searchTerm,
                    sortOption,
                    component: 'pending'
                }
            });
        }
    }, [totalNumberOfRecords, currentPage, searchTerm, sortOption, dispatch]);

    // Scroll to top
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [currentPage]);

    // Load thumbnails
    useEffect(() => {
        if (data?.length > 0 && jwtToken && env !== "standalone") {
            const dataHash = JSON.stringify(data.map(v => v._id));
            if (thumbnailsLoadedRef.current === dataHash) return;

            const loadThumbnails = async () => {
                const videosNeedingThumbnails = data.filter(video => !videoThumbnails[video._id]);
                if (videosNeedingThumbnails.length === 0) {
                    thumbnailsLoadedRef.current = dataHash;
                    return;
                }

                const loadingStates = {};
                videosNeedingThumbnails.forEach(video => loadingStates[video._id] = true);
                setThumbnailLoading(prev => ({ ...prev, ...loadingStates }));

                const thumbnailPromises = videosNeedingThumbnails.map(async (video) => {
                    try {
                        const thumbnailUrl = await getFetchGamePostFileThumbnail(jwtToken, video._id, video.game[0]?.tnPath);
                        return { id: video._id, url: thumbnailUrl };
                    } catch (error) {
                        return { id: video._id, url: null };
                    }
                });

                const results = await Promise.all(thumbnailPromises);
                const newThumbnails = { ...videoThumbnails };
                results.forEach(({ id, url }) => { if (url) newThumbnails[id] = url; });

                setVideoThumbnails(newThumbnails);
                thumbnailsLoadedRef.current = dataHash;
                setThumbnailLoading(prev => {
                    const newState = { ...prev };
                    Object.keys(loadingStates).forEach(id => newState[id] = false);
                    return newState;
                });
            };
            loadThumbnails();
        }
    }, [data, jwtToken, env]);

    // Handle video click
    const handlePlayClick = useCallback((video) => {
        try { ReactGA.event({ category: 'CoachFlow', action: 'pending_play_click' }); } catch { }
        localStorage.setItem("reviewPostId", video._id);
        localStorage.setItem(`${video._id}_reviewPost`, JSON.stringify(video));
        localStorage.setItem("videoLinkInObjectStore", video.game[0].path);

        if (env !== "standalone") {
            const { firstName, lastName } = video?.poster?.profileData?.name || {};
            localStorage.setItem("firstname", firstName || "");
            localStorage.setItem("lastname", lastName || "");
        }

        navigate(`/coach_flow/pending-video/feedback/${video._id}`, {
            state: { videoTags: video.tags, aiFeedbackState: video?.aiFeedbackState || "created" }
        });
    }, [env, navigate]);

    const handleSearchChange = (text) => updateSearchTerm(text);
    const handleSortChange = (val) => updateSortOption(val);
    const handlePageChange = (page) => goToPage(page);

    const filteredVideos = useMemo(() => env === "standalone" ? data?.filter(video => video.state === "review_requested") || [] : data || [], [env, data]);

    return (
        <div className="w-full pb-10 pt-32">
            {/* Header Section */}
            <div className="flex flex-col gap-6 mb-8">
                <div>
                    <h1 className="text-4xl font-extrabold text-[#15803d]">Video Queue</h1>
                </div>

                <div className="flex flex-col md:flex-row items-center gap-4 w-full">
                    <div className="flex-1 w-full">
                        <SearchVideos
                            setSearchText={handleSearchChange}
                            existingSearchText={searchTerm}
                            placeholderText="Search videos..."
                            className="w-full"
                        />
                    </div>
                    <div className="w-full md:w-48 shrink-0">
                        <CustomDropdown
                            options={sortOptions}
                            selectedValue={sortOption}
                            onChange={handleSortChange}
                            label=""
                        />
                    </div>
                </div>
            </div>

            {/* Content Section */}
            {isStarted ? (
                <Box sx={{ display: 'flex', justifyContent: "center", alignItems: "center", height: "50vh" }}>
                    <CircularProgress sx={{ color: '#059669' }} />
                </Box>
            ) : (
                <>
                    {/* Video Grid */}
                    {filteredVideos.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {filteredVideos.map((video) => {
                                const thumbnailUrl = videoThumbnails[video._id] || thumbnail;
                                const isLoading = thumbnailLoading[video._id];

                                return (
                                    <div
                                        key={video._id}
                                        onClick={() => handlePlayClick(video)}
                                        className="group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 cursor-pointer flex flex-col h-full transform hover:-translate-y-1"
                                    >
                                        {/* Thumbnail Container */}
                                        <div className="relative aspect-video bg-gray-100 overflow-hidden">
                                            {/* Thumbnail Image */}
                                            <div
                                                className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                                                style={{ backgroundImage: `url(${thumbnailUrl})` }}
                                            />

                                            {/* Overlay Gradient */}
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-60" />

                                            {/* Play Icon - Centered */}
                                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                                                <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/50 text-white">
                                                    <FontAwesomeIcon icon={faCirclePlay} className="text-2xl" />
                                                </div>
                                            </div>

                                            {/* Loading State */}
                                            {isLoading && (
                                                <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-20">
                                                    <CircularProgress size={24} sx={{ color: '#059669' }} />
                                                </div>
                                            )}
                                        </div>

                                        {/* Content Container */}
                                        <div className="p-4 flex-1 flex flex-col">
                                            {/* Header: Title and Status */}
                                            <div className="flex justify-between items-start mb-2">
                                                <h3 className="font-semibold text-gray-900 line-clamp-1 group-hover:text-emerald-600 transition-colors" title={extractTitle(video.game[0]?.path)}>
                                                    {truncateTitle(extractTitle(video.game[0]?.path))}
                                                </h3>
                                                {video.coachManualFeedback && (
                                                    <div className="text-emerald-500" title="Has feedback">
                                                        <FontAwesomeIcon icon={faComment} size="sm" />
                                                    </div>
                                                )}
                                            </div>

                                            {/* Metadata: Player & Date */}
                                            <div className="space-y-2 mt-1">
                                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                                    <FontAwesomeIcon icon={faUser} className="w-3" />
                                                    <span className="font-medium text-gray-700">{extractPlayerName(video)}</span>
                                                </div>
                                                <div className="flex items-center gap-2 text-xs text-gray-400">
                                                    <FontAwesomeIcon icon={faCalendar} className="w-3" />
                                                    <span>{dataFormatter(video.updatedAt)}</span>
                                                </div>
                                            </div>

                                            {/* Action Button */}
                                            <div className="mt-4 pt-3 border-t border-gray-50 flex justify-center">
                                                <button
                                                    className="w-full py-2 bg-[#15803d] text-white rounded-lg text-sm font-medium hover:bg-[#14532d] transition-colors flex items-center justify-center gap-2"
                                                >
                                                    <span>Review Video</span>
                                                    <FontAwesomeIcon icon={faCirclePlay} size="xs" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-gray-100 shadow-sm">
                            <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mb-4">
                                <FontAwesomeIcon icon={faCirclePlay} className="text-3xl text-emerald-200" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">No videos found</h3>
                            <p className="text-gray-500 mt-1">
                                {searchTerm ? "Try adjusting your search terms" : "Videos awaiting feedback will appear here"}
                            </p>
                        </div>
                    )}

                    {/* Use SearchUploadedVideoPagination directly */}
                    {totalPages > 1 && (
                        <div className="mt-8 flex justify-center">
                            <SearchUploadedVideoPagination
                                totalNumberofPages={totalPages}
                                currentPage={currentPage}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}
                </>
            )}

            <ToastContainer
                position="top-right"
                autoClose={5000}
                theme="colored"
            />
        </div>
    );
};

export default Pending;
